import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Printer, Download, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

export const OrderPrintView = ({ order, store, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [printing, setPrinting] = useState(false);
  const printRef = useRef(null);

  const isDelivery = order.delivery_type === 'delivery';

  // Calculate totals correctly - FORCE delivery fee to show
  const itemsSubtotal = order.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const deliveryFee = parseFloat(order.delivery_fee) || 0;
  const discount = parseFloat(order.discount) || 0;
  const finalTotal = order.total || (itemsSubtotal + deliveryFee - discount);
  
  // Debug log
  console.log('OrderPrintView - order.delivery_fee:', order.delivery_fee, 'parsed:', deliveryFee);

  // Generate print HTML
  const generatePrintHTML = () => {
    const items = order.items.map(item => `
      <tr>
        <td style="padding: 8px 0; border-bottom: 2px dotted #000;">
          <strong>${item.quantity}x ${item.product_name}</strong>
          ${item.variant_name ? `<br><span style="font-size: 12px;">• ${item.variant_name}</span>` : ''}
          ${item.notes ? `<br><span style="font-size: 12px; font-style: italic;">Obs: ${item.notes}</span>` : ''}
        </td>
        <td style="padding: 8px 0; border-bottom: 2px dotted #000; text-align: right; white-space: nowrap;">
          <strong>R$ ${(item.unit_price * item.quantity).toFixed(2)}</strong>
        </td>
      </tr>
    `).join('');

    return `
      <div style="font-family: 'Courier New', Courier, monospace; font-size: 14px; font-weight: bold; width: 280px; padding: 10px; margin: 0 auto; color: #000; background: #fff;">
        <div style="text-align: center; font-size: 18px; margin-bottom: 5px;">${store?.name || 'ADEGGA DELIVERY'}</div>
        ${store?.whatsapp ? `<div style="text-align: center; font-size: 14px; margin-bottom: 15px;">Tel: ${store.whatsapp}</div>` : ''}
        
        <div style="border-top: 3px solid #000; margin: 12px 0;"></div>
        
        <div style="font-size: 20px;">PEDIDO #${order.order_number}</div>
        <div style="font-size: 14px; margin-top: 5px;">${new Date(order.created_at).toLocaleString('pt-BR')}</div>
        
        <div style="border-top: 3px solid #000; margin: 12px 0;"></div>

        <div style="margin: 8px 0; font-size: 14px;">CLIENTE: ${order.customer_name}</div>
        <div style="margin: 8px 0; font-size: 14px;">TEL: ${order.customer_phone}</div>
        <div style="margin: 8px 0; font-size: 14px;">TIPO: ${isDelivery ? '★★★ ENTREGA ★★★' : '★★★ RETIRADA ★★★'}</div>

        ${isDelivery && order.address ? `
          <div style="background: #eee; padding: 12px; margin: 12px 0;">
            <div style="margin-bottom: 8px;">📍 ENDERECO:</div>
            <div>${order.address.street || ''}, ${order.address.number || ''}</div>
            ${order.address.complement ? `<div>${order.address.complement}</div>` : ''}
            <div>${order.address.neighborhood || ''} - ${order.address.city || ''}</div>
            ${order.address.reference ? `<div style="margin-top: 8px;">REF: ${order.address.reference}</div>` : ''}
          </div>
        ` : ''}

        <div style="border-top: 3px dashed #000; margin: 12px 0;"></div>
        
        <div style="font-size: 16px; margin-bottom: 10px;">📦 ITENS:</div>
        <table style="width: 100%; border-collapse: collapse;">
          ${items}
        </table>
        
        <div style="border-top: 3px dashed #000; margin: 12px 0;"></div>

        <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 16px;">
          <span>SUBTOTAL:</span>
          <span>R$ ${itemsSubtotal.toFixed(2)}</span>
        </div>
        ${isDelivery ? `
          <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 16px; color: #ff6b00;">
            <span>TAXA ENTREGA:</span>
            <span>R$ ${deliveryFee.toFixed(2)}</span>
          </div>
        ` : ''}
        ${discount > 0 ? `
          <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 16px;">
            <span>DESCONTO:</span>
            <span>-R$ ${discount.toFixed(2)}</span>
          </div>
        ` : ''}
        
        <div style="border-top: 3px solid #000; margin: 12px 0;"></div>

        <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 24px;">
          <span>💰 TOTAL:</span>
          <span>R$ ${finalTotal.toFixed(2)}</span>
        </div>
        
        <div style="border-top: 3px solid #000; margin: 12px 0;"></div>

        <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 16px;">
          <span>PAGAMENTO:</span>
          <span>${order.payment_method === 'pix' ? '📱 PIX' : order.payment_method === 'cash' ? '💵 DINHEIRO' : '💳 CARTAO'}</span>
        </div>
        ${order.payment_method === 'cash' && order.change_for ? `
          <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 16px;">
            <span>TROCO PARA:</span>
            <span>R$ ${order.change_for}</span>
          </div>
        ` : ''}

        ${order.notes ? `
          <div style="border-top: 3px dashed #000; margin: 12px 0;"></div>
          <div style="font-size: 14px;">📝 OBS: ${order.notes}</div>
        ` : ''}

        <div style="border-top: 3px dashed #000; margin: 12px 0;"></div>
        
        <div style="text-align: center; font-size: 14px;">
          <div>OBRIGADO PELA PREFERENCIA!</div>
          <div style="margin-top: 5px;">★ ADEGGA DELIVERY ★</div>
        </div>
      </div>
    `;
  };

  // MAIN PRINT FUNCTION - Direct print
  const handlePrint = useCallback(() => {
    setPrinting(true);
    
    // Create hidden iframe for printing
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = 'none';
    document.body.appendChild(printFrame);

    const printDocument = printFrame.contentWindow.document;
    printDocument.open();
    printDocument.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Pedido #${order.order_number}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          @media print { 
            html, body { width: 80mm; margin: 0; padding: 0; }
          }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { background: white; }
        </style>
      </head>
      <body>
        ${generatePrintHTML()}
      </body>
      </html>
    `);
    printDocument.close();

    // Wait for content to load then print
    printFrame.onload = () => {
      setTimeout(() => {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          toast.success('Comando de impressao enviado!');
        } catch (e) {
          console.error('Print error:', e);
          toast.error('Erro ao imprimir');
        } finally {
          setPrinting(false);
          // Remove iframe after print dialog closes
          setTimeout(() => {
            if (printFrame.parentNode) {
              printFrame.parentNode.removeChild(printFrame);
            }
          }, 1000);
        }
      }, 250);
    };

    // Fallback timeout
    setTimeout(() => {
      if (printing) {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
        } catch (e) {}
        setPrinting(false);
      }
    }, 2000);
  }, [order, store, isDelivery, itemsSubtotal, deliveryFee, discount, finalTotal]);

  // Keyboard shortcut: Ctrl + Shift + P
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        e.stopPropagation();
        handlePrint();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [handlePrint]);

  // Generate text version
  const generateText = () => {
    return `
================================================
         ${store?.name || 'ADEGGA DELIVERY'}
${store?.whatsapp ? `         Tel: ${store.whatsapp}` : ''}
================================================

PEDIDO #${order.order_number}
${new Date(order.created_at).toLocaleString('pt-BR')}

CLIENTE: ${order.customer_name}
TEL: ${order.customer_phone}
TIPO: ${isDelivery ? '*** ENTREGA ***' : '*** RETIRADA ***'}
${isDelivery && order.address ? `
ENDERECO:
${order.address.street || ''}, ${order.address.number || ''}
${order.address.complement || ''}
${order.address.neighborhood || ''} - ${order.address.city || ''}
${order.address.reference ? `REF: ${order.address.reference}` : ''}
` : ''}
------------------------------------------------
ITENS:
${order.items.map(item => `${item.quantity}x ${item.product_name}  R$ ${(item.unit_price * item.quantity).toFixed(2)}`).join('\n')}
------------------------------------------------
SUBTOTAL:     R$ ${itemsSubtotal.toFixed(2)}
${deliveryFee > 0 ? `TAXA ENTREGA: R$ ${deliveryFee.toFixed(2)}` : ''}
${discount > 0 ? `DESCONTO:    -R$ ${discount.toFixed(2)}` : ''}
================================================
TOTAL:        R$ ${finalTotal.toFixed(2)}
================================================
PAGAMENTO: ${order.payment_method === 'pix' ? 'PIX' : order.payment_method === 'cash' ? 'DINHEIRO' : 'CARTAO'}
${order.payment_method === 'cash' && order.change_for ? `TROCO PARA: R$ ${order.change_for}` : ''}
${order.notes ? `\nOBS: ${order.notes}` : ''}

OBRIGADO PELA PREFERENCIA!
ADEGGA DELIVERY
    `.trim();
  };

  // Copy to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(generateText());
    setCopied(true);
    toast.success('Copiado! Cole no Bloco de Notas e imprima com Ctrl+P');
    setTimeout(() => setCopied(false), 3000);
  };

  // Download TXT
  const handleDownloadTxt = () => {
    const text = generateText();
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pedido-${order.order_number}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Arquivo baixado! Abra e imprima com Ctrl+P');
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#13131f] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Printer className="w-5 h-5 text-[#ff6b00]" />
            Imprimir Pedido #{order.order_number}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Opcoes de impressao
          </DialogDescription>
        </DialogHeader>

        {/* Quick Preview */}
        <div className="bg-white text-black p-3 rounded-lg font-mono text-sm font-bold">
          <div className="text-center text-base mb-2">{store?.name || 'ADEGGA DELIVERY'}</div>
          <div className="border-t-2 border-black my-2"></div>
          <div className="text-lg">PEDIDO #{order.order_number}</div>
          <div className="text-sm mb-2">{new Date(order.created_at).toLocaleString('pt-BR')}</div>
          <div>CLIENTE: {order.customer_name}</div>
          <div>TIPO: {isDelivery ? '★★★ ENTREGA ★★★' : '★★★ RETIRADA ★★★'}</div>
          <div className="border-t border-dashed border-black my-2"></div>
          {order.items.slice(0, 3).map((item, i) => (
            <div key={i} className="flex justify-between">
              <span>{item.quantity}x {item.product_name}</span>
              <span>R$ {(item.unit_price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          {order.items.length > 3 && <div className="text-gray-500">... +{order.items.length - 3} itens</div>}
          <div className="border-t-2 border-black my-2"></div>
          <div className="flex justify-between">
            <span>SUBTOTAL:</span>
            <span>R$ {itemsSubtotal.toFixed(2)}</span>
          </div>
          {isDelivery && (
            <div className="flex justify-between text-[#ff6b00]">
              <span>TAXA ENTREGA:</span>
              <span>R$ {deliveryFee.toFixed(2)}</span>
            </div>
          )}
          {discount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>DESCONTO:</span>
              <span>-R$ {discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-xl mt-2 pt-2 border-t-2 border-black">
            <span>💰 TOTAL:</span>
            <span>R$ {finalTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* Main Print Button */}
        <Button
          onClick={handlePrint}
          disabled={printing}
          className="w-full bg-[#ff6b00] hover:bg-[#ff8533] h-16 text-xl font-bold"
          data-testid="main-print-btn"
        >
          {printing ? (
            <>
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent mr-3"></div>
              IMPRIMINDO...
            </>
          ) : (
            <>
              <Printer className="w-6 h-6 mr-3" />
              IMPRIMIR
            </>
          )}
        </Button>
        
        <p className="text-center text-white/60 text-sm -mt-2">
          Atalho: <span className="font-bold text-white/80">Ctrl + Shift + P</span>
        </p>

        {/* Alternative: Copy */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={handleCopy}
            variant="outline"
            className={`h-14 border-white/20 text-white hover:bg-white/10 ${copied ? 'bg-green-500/20 border-green-500' : ''}`}
          >
            <Copy className="w-5 h-5 mr-2" />
            {copied ? 'COPIADO!' : 'COPIAR TEXTO'}
          </Button>
          
          <Button
            onClick={handleDownloadTxt}
            variant="outline"
            className="h-14 border-white/20 text-white hover:bg-white/10"
          >
            <Download className="w-5 h-5 mr-2" />
            BAIXAR TXT
          </Button>
        </div>

        {/* Help */}
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <p className="text-yellow-400 font-bold text-sm mb-2">Se nao imprimir:</p>
          <ul className="text-white/80 text-sm space-y-1 list-disc list-inside">
            <li>Verifique se a impressora esta <strong>ligada</strong></li>
            <li>Defina como <strong>impressora padrao</strong> no Windows</li>
            <li>Use <strong>"COPIAR TEXTO"</strong> e imprima pelo Bloco de Notas</li>
          </ul>
        </div>

        <Button onClick={onClose} variant="ghost" className="w-full text-white/60">
          Fechar
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default OrderPrintView;
